import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  Switch,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import currency from 'currency.js';
import { Controller, Path, useForm, useWatch } from 'react-hook-form';
import { gql, useMutation, useQuery } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import { useToggleContext } from '../../contexts/toggle';
import {
  AdvancePickEmWeekMutation,
  AdvancePickEmWeekMutationVariables,
  FetchPickEmNextWeekGamesQuery,
  FetchPickEmPayoutLeaguesQuery,
  PullNextWeekScoresMutation,
  PullNextWeekScoresMutationVariables,
} from '../../generated/hooks';
import NumericField from '../forms/NumericField';
import { TeamLogoComponent } from '../logos';

const fetchPickEmPayoutLeagues = gql`
  query fetchPickEmPayoutLeagues {
    pickEmPayoutLeagues {
      id
      name
      members {
        id
        user {
          id
          fullName
        }
        currentWeekScore
        totalScore
        totalPayout
      }
    }
  }
`;

const fetchPickEmNextWeekGames = gql`
  query fetchPickEmNextWeekGames {
    pickEmNextWeekGames {
      selected
      game {
        id
        kickoffDateTime
        homeOdds
        homeTeam {
          id
          name
          styles {
            primaryColor
            secondaryColor
            logoComponent
          }
        }
        awayTeam {
          id
          name
          styles {
            primaryColor
            secondaryColor
            logoComponent
          }
        }
      }
    }
  }
`;

const advancePickEmWeek = gql`
  mutation advancePickEmWeek($input: AdvancePickEmWeekInputType!) {
    advancePickEmWeek(input: $input) {
      ok
      membersNotified
      toggle {
        id
        name
        on
        value
      }
    }
  }
`;

const pullNextWeekScores = gql`
  mutation pullNextWeekScores {
    pullNextWeekScores {
      ok
    }
  }
`;

// Home-team spread: optional sign, 1-2 integer digits, optional single decimal (matches the DB's
// numeric(3,1) column). 0 is a valid pick'em line.
const SPREAD_PATTERN = /^-?\d{1,2}(\.\d)?$/;

type FormValues = {
  amounts: Record<string, string>;
  games: Record<string, { selected: boolean; homeOdds: string }>;
};

function formatKickoff(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function AdminWeeklyPayouts() {
  const { getToggle } = useToggleContext();
  const { flashMutationError } = useFlashMessageContext();
  const [confirmation, setConfirmation] = useState<string>();
  const [hideUnchecked, setHideUnchecked] = useState(false);

  const [{ data: leaguesData, error: leaguesError, fetching: leaguesFetching }, reexecuteLeagues] =
    useQuery<FetchPickEmPayoutLeaguesQuery>({ query: fetchPickEmPayoutLeagues });
  const [{ data: gamesData, error: gamesError, fetching: gamesFetching }, reexecuteGames] =
    useQuery<FetchPickEmNextWeekGamesQuery>({ query: fetchPickEmNextWeekGames });
  const leagues = leaguesData?.pickEmPayoutLeagues ?? [];
  const candidateGames = gamesData?.pickEmNextWeekGames ?? [];

  // Current season + week are global toggles, independent of any league.
  const currentSeasonToggle = getToggle('currentSeason')?.value;
  const season = currentSeasonToggle ? Number(currentSeasonToggle) : undefined;
  const currentWeekToggle = getToggle('currentWeek')?.value;
  const week =
    season !== undefined && currentWeekToggle ? (JSON.parse(currentWeekToggle)[season] as number) : undefined;

  const {
    clearErrors,
    control,
    getValues,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { amounts: {}, games: {} },
  });

  // Each member pays $1 into the weekly pot, so a league's payout inputs must sum to its member
  // count. Validate this live off the watched amounts and drive it through react-hook-form's error
  // state: a manual error on this synthetic per-league name blocks handleSubmit and clears itself as
  // soon as the totals balance. `payoutTotals` isn't part of FormValues, so the name is cast.
  const payoutTotalErrorName = (leagueId: string) => `payoutTotals.${leagueId}` as unknown as Path<FormValues>;
  const payoutTotalErrors = (errors as { payoutTotals?: Record<string, { message?: string }> }).payoutTotals;
  const payoutTotalError = (leagueId: string) => payoutTotalErrors?.[leagueId]?.message;
  const leaguePayoutTotal = (members: { id: string }[], amounts: FormValues['amounts'] | undefined) =>
    members.reduce((sum, member) => sum.add(amounts?.[member.id]?.trim() || '0'), currency(0));

  const watchedAmounts = useWatch({ control, name: 'amounts' }) as FormValues['amounts'] | undefined;
  useEffect(() => {
    leagues.forEach(league => {
      const members = (league.members ?? []).filter((m): m is NonNullable<typeof m> => m != null);
      const total = leaguePayoutTotal(members, watchedAmounts);
      if (total.value === members.length) {
        clearErrors(payoutTotalErrorName(league.id));
      } else {
        setError(payoutTotalErrorName(league.id), {
          type: 'validate',
          message:
            `Payouts total ${total.format()}, but must equal ${currency(members.length).format()} ` +
            `(${members.length} player${members.length === 1 ? '' : 's'}).`,
        });
      }
    });
  }, [watchedAmounts, leagues]);

  // Watch the checkbox state so the table re-orders live (checked first, then kickoff, then home team)
  // and the "Include" header can show a running count.
  const watchedGames = useWatch({ control, name: 'games' }) as FormValues['games'] | undefined;
  const selectedCount = candidateGames.filter(({ game }) => watchedGames?.[game.id]?.selected).length;
  const sortedGames = [...candidateGames].sort((a, b) => {
    const aSelected = watchedGames?.[a.game.id]?.selected ? 1 : 0;
    const bSelected = watchedGames?.[b.game.id]?.selected ? 1 : 0;
    return (
      bSelected - aSelected ||
      new Date(a.game.kickoffDateTime).getTime() - new Date(b.game.kickoffDateTime).getTime() ||
      a.game.homeTeam.name.localeCompare(b.game.homeTeam.name)
    );
  });
  const displayedGames = hideUnchecked
    ? sortedGames.filter(({ game }) => watchedGames?.[game.id]?.selected)
    : sortedGames;

  // Seed the form when data loads (and again after a submit advances the week): blank payouts, and each
  // candidate game pre-checked to its current slate membership with its saved spread pre-filled.
  useEffect(() => {
    if (!leaguesData || !gamesData) {
      return;
    }
    const amounts: Record<string, string> = {};
    leagues.forEach(league => league.members.forEach(member => (amounts[member.id] = '')));
    const games: FormValues['games'] = {};
    candidateGames.forEach(({ game, selected }) => {
      games[game.id] = { selected, homeOdds: game.homeOdds != null ? String(game.homeOdds) : '' };
    });
    reset({ amounts, games });
  }, [leaguesData, gamesData, week]);

  const [, advancePickEmWeekMutation] = useMutation<AdvancePickEmWeekMutation, AdvancePickEmWeekMutationVariables>(
    advancePickEmWeek,
  );

  const [{ fetching: pulling }, pullNextWeekScoresMutation] = useMutation<
    PullNextWeekScoresMutation,
    PullNextWeekScoresMutationVariables
  >(pullNextWeekScores);
  // Pull next week's times + spreads from theScore, then refetch so the slate re-seeds (the effect
  // above resets the form from the refreshed games, pre-filling spreads).
  const handlePullScores = () =>
    pullNextWeekScoresMutation({}).then(response => {
      if (flashMutationError(response.error)) {
        return;
      }
      reexecuteGames({ requestPolicy: 'network-only' });
    });
  const handleAdvance = handleSubmit(values => {
    // Belt-and-suspenders: the live per-league errors above already block the button, but never write
    // payouts unless every league's inputs sum to its member count.
    const balanced = leagues.every(league => {
      const members = (league.members ?? []).filter((m): m is NonNullable<typeof m> => m != null);
      return leaguePayoutTotal(members, values.amounts).value === members.length;
    });
    if (!balanced) {
      return;
    }
    const payouts = leagues.flatMap(league =>
      league.members.map(member => ({
        leagueMemberId: member.id,
        amount: values.amounts[member.id]?.trim() ? values.amounts[member.id].trim() : '0',
      })),
    );
    const games = candidateGames
      .filter(({ game }) => values.games[game.id]?.selected)
      .map(({ game }) => ({ gameId: game.id, homeOdds: values.games[game.id].homeOdds.trim() }));
    return advancePickEmWeekMutation({ input: { payouts, games } }).then(response => {
      if (flashMutationError(response.error)) {
        return;
      }
      const count = response.data?.advancePickEmWeek?.membersNotified ?? 0;
      setConfirmation(
        `Saved. Advanced to Week ${(week ?? 0) + 1}, set ${games.length} game${games.length === 1 ? '' : 's'}, ` +
          `notified ${count} member${count === 1 ? '' : 's'}.`,
      );
      reexecuteLeagues({ requestPolicy: 'network-only' });
      reexecuteGames({ requestPolicy: 'network-only' });
    });
  });

  if ((leaguesFetching && !leaguesData) || (gamesFetching && !gamesData)) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const error = leaguesError ?? gamesError;
  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="error">Failed to load weekly payouts: {error.message}</Alert>
      </Box>
    );
  }

  if (week === undefined) {
    return (
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="warning">Could not determine the current season/week from the global toggles.</Alert>
      </Box>
    );
  }

  if (leagues.length === 0) {
    return (
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Weekly Payouts — Week {week}
        </Typography>
        <Typography variant="body1">No Pick &apos;Em leagues have payouts enabled this season.</Typography>
      </Box>
    );
  }

  const showTable = candidateGames.length !== 0;

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Weekly Payouts — Week {week}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter each winner&apos;s payout for Week {week}, choose next week&apos;s games and set their spreads, then
        submit. This records the payouts, notifies every member of their weekly results, builds the Week {week + 1}{' '}
        slate, and advances the season to Week {week + 1} (which also drives score pulling).
      </Typography>

      <form onSubmit={handleAdvance}>
        {leagues.map(league => (
          <Box key={league.id} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {league.name}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Week {week} correct</TableCell>
                    <TableCell align="right">Season total</TableCell>
                    <TableCell align="right">Season payout</TableCell>
                    <TableCell align="right">Payout ($)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...league.members]
                    .sort(
                      (a, b) =>
                        b.currentWeekScore - a.currentWeekScore ||
                        b.totalScore - a.totalScore ||
                        a.user.fullName.localeCompare(b.user.fullName),
                    )
                    .map(member => (
                      <TableRow key={member.id}>
                        <TableCell>{member.user.fullName}</TableCell>
                        <TableCell align="right">{member.currentWeekScore}</TableCell>
                        <TableCell align="right">{member.totalScore}</TableCell>
                        <TableCell align="right">{currency(member.totalPayout).format()}</TableCell>
                        <TableCell align="right">
                          <NumericField
                            control={control}
                            label=""
                            name={`amounts.${member.id}`}
                            decimalScale={2}
                            rules={{ pattern: { value: /^\d*\.?\d{0,2}$/, message: 'Enter a dollar amount' } }}
                            sx={{ width: 110 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            {payoutTotalError(league.id) && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {payoutTotalError(league.id)}
              </Typography>
            )}
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h6">Games for Week {week + 1}</Typography>
          {showTable && (
            <>
              <Box sx={{ flexGrow: 1 }}>
                <Button variant="outlined" size="small" onClick={handlePullScores} disabled={pulling}>
                  {pulling ? 'Pulling…' : 'Pull Scores'}
                </Button>
              </Box>
              <FormControlLabel
                control={<Switch checked={hideUnchecked} onChange={e => setHideUnchecked(e.target.checked)} />}
                label="Hide unchecked games"
              />
            </>
          )}
        </Box>
        {!showTable ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            No scheduled games found for Week {week + 1}.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Include ({selectedCount})</TableCell>
                  <TableCell>Away</TableCell>
                  <TableCell>Home</TableCell>
                  <TableCell>Kickoff</TableCell>
                  <TableCell align="right">Home spread</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedGames.map(({ game }) => (
                  <TableRow key={game.id}>
                    <TableCell padding="checkbox">
                      <Controller
                        control={control}
                        name={`games.${game.id}.selected`}
                        render={({ field: { value, onChange } }) => (
                          <Checkbox checked={!!value} onChange={e => onChange(e.target.checked)} />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <TeamLogoComponent styles={game.awayTeam.styles} />
                        {game.awayTeam.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          @
                        </Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <TeamLogoComponent styles={game.homeTeam.styles} />
                          {game.homeTeam.name}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{formatKickoff(game.kickoffDateTime)}</TableCell>
                    <TableCell align="right">
                      <NumericField
                        control={control}
                        label=""
                        name={`games.${game.id}.homeOdds`}
                        numberType="negativeDecimal"
                        decimalScale={1}
                        rules={{
                          validate: value =>
                            !getValues(`games.${game.id}.selected`) ||
                            SPREAD_PATTERN.test(String(value ?? '').trim()) ||
                            'Enter a valid spread',
                        }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : `Save & Advance to Week ${week + 1}`}
          </Button>
          {confirmation && (
            <Typography variant="body2" color="success.main">
              {confirmation}
            </Typography>
          )}
        </Box>
      </form>
    </Box>
  );
}

export default AdminWeeklyPayouts;
